import { useCallback, useDebugValue, useEffect, useState } from 'react';
import { usePrevious } from '@/hooks/use-previous';
import isNil from 'lodash-es/isNil';

export const useLocalStorage = <S>(
  key: string,
  initialState?: S | ((item: string | null) => S),
): [S, React.Dispatch<React.SetStateAction<S>>] => {
  const prevKey = usePrevious(key);

  const getItemWithDefault = useCallback(
    (keyToRead: string) => {
      const item = localStorage.getItem(keyToRead);
      if (item) {
        return parse(item);
      } else if (typeof initialState === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        return (initialState as (item: string | null) => S)(item);
      } else {
        return initialState as S; // eslint-disable-line @typescript-eslint/no-unsafe-type-assertion
      }
    },
    [initialState],
  );

  const [state, setState] = useState<S>(() => {
    return getItemWithDefault(key);
  });

  useDebugValue(state);

  // 키가 변경될 때 상태 업데이트 (첫 렌더 제외 - useState 초기값에서 처리됨)
  useEffect(() => {
    if (prevKey === undefined || prevKey === key) {
      return;
    }

    const value = getItemWithDefault(key);
    setState(value);
  }, [key, prevKey, getItemWithDefault]);

  // 상태가 변경될 때 localStorage 업데이트
  useEffect(() => {
    if (prevKey !== key) {
      return;
    }
    // if (key.startsWith('chart.timeframes')) {
    //   console.log(`useLocalStorage ${key} set`, state);
    // }
    if (isNil(state)) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(state));
    }
  }, [key, prevKey, state]);

  // key 가 달라진 경우 state 대신 실제 localStorage 값을 반환
  // 이유: return [state, setState] 만 사용하면 key가 달라질 때마다 달라지기 전 key용 state가 1회 반환되는데,
  // chart.widths.XXX 키의 경우 잘못된 state를 Allotment.defaultSizes 에 적용하여 차트 너비가 초기화되는 문제가 발생함.
  // 그렇다고 대신에 return [getItemWithDefault(key), setState];를 항상 사용하면
  // state 변경에 따른 UI 갱신이 트리거되지 않게 되어, 다크모드 토글이 즉시 반영되지 않는 오류가 발생하게 됨.
  // 그래서 키 변경 시에 한해서 localStorage 값을 반환하도록 함.
  // 이 문제를 usehooks-ts 나 mantine 등으로 대체하여 해결해보려했으나 동일한 증상이 발생하여 아래 방법으로 해결함.
  if (prevKey !== key) {
    return [getItemWithDefault(key), setState];
  }

  return [state, setState];
};

const parse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};
